import { useMemo } from 'react';
import { EventPageType } from '@graphql/generated-base';
import type { PageContentType, PageKind } from '@graphql/generated-base';
import { isUserGeneratedMigratedSquatter, toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';

export type MinimalPage = Readonly<{
  pageSlug: string;
  type: EventPageType;
  // CORE-6470: carried so callers (e.g. useEventPageRoute) can detect userGenerated migrated
  // squatters via pageKind === userCustom. `contentType` stays for transitional callers.
  pageKind?: PageKind | null;
  contentType?: PageContentType | null;
}>;

type UseDefaultPageReturn<T extends MinimalPage> = Readonly<{
  defaultPage: Readonly<T> | undefined;
  isDefaultPage: (pageSlug: string, type?: EventPageType) => boolean;
  loading: boolean;
}>;

// RSVP and Registry are standalone features with their own routes (RSVP lives in the legacy
// monorepo; Registry has a dedicated /registry landing). They must never become the website's
// default page — the default page's nav link collapses to the bare root URL (see
// `useEventPageRoute.goToPath`), which would point the RSVP/Registry link at `/:eventHandle`
// instead of its own route. Covers the legacy types and the migrated registry custom page
// (type=custom, slug canonicalizes to 'registrylinks'). A user-generated page that merely squats
// the 'registrylinks' slug is ordinary website content, so it stays eligible as the default page.
const isStandaloneFeaturePage = (page: MinimalPage): boolean =>
  page.type === EventPageType.rsvp ||
  page.type === EventPageType.registry ||
  (toCanonicalMigratedSlug(page.pageSlug) === 'registrylinks' && !isUserGeneratedMigratedSquatter(page));

/**
 * Hook that determines the default page for an event.
 *
 * The default page is:
 * 1. The welcome page (if it exists)
 * 2. Otherwise the first non-feature page (RSVP/Registry are standalone features, never the
 *    website landing). Feature-only events have no default page (`undefined`) — they are
 *    redirected to their feature route by `useGuestSiteRedirection`.
 *
 * This is used for:
 * - Rendering content at the root route `/:eventHandle/`
 * - Generating navbar links (default page links to root)
 * - Updating URL when scrolling (default page uses root URL)
 */
export const useDefaultPage = <T extends MinimalPage>(pages?: ReadonlyArray<T>): UseDefaultPageReturn<T> => {
  const { checkIsWelcomePage, loading } = useWelcomePage();

  const defaultPage = useMemo(() => {
    if (!pages?.length || loading) {
      return undefined;
    }

    const welcomePage = pages.find(page => checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage);
    // Fall back to the first genuine website page, never a standalone feature page (RSVP/Registry).
    return welcomePage ?? pages.find(page => !isStandaloneFeaturePage(page));
  }, [pages, checkIsWelcomePage, loading]);

  const isDefaultPage = (pageSlug: string, type?: EventPageType) => {
    if (!defaultPage) {
      return false;
    }

    if (defaultPage.pageSlug !== pageSlug) {
      return false;
    }

    // When type is provided, also match on type to disambiguate pages that share the same slug
    // (e.g., a legacy welcome page and a custom page both with pageSlug 'welcome').
    if (type && defaultPage.type !== type) {
      return false;
    }

    return true;
  };

  return {
    /** The default page object (welcome page or first page) */
    defaultPage,
    /** Function to check if a given slug is the default page */
    isDefaultPage,
    /** Whether the hook is still loading (waiting for feature flags) */
    loading,
  };
};
